import { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  FINANCIAL_ACKNOWLEDGMENT_TEXT,
  REQUIRED_LEGAL_DOCUMENTS,
} from '@perdiem/trpc/legal';
import { trpc } from '../lib/trpc';
import {
  consumePendingLegalSignupAcceptance,
  getLegalClientContext,
} from '../lib/legal-client';
import { getLegalUrl, openExternalUrl } from '../lib/utils';
import { supabase } from '../lib/supabase';
import { Button } from './Button';
import { LoadingSpinner } from './LoadingSpinner';

type LegalTermsGateProps = {
  enabled: boolean;
};

const TERMS_URL = getLegalUrl('terms');
const PRIVACY_URL = getLegalUrl('privacy');

export function LegalTermsGate({ enabled }: LegalTermsGateProps) {
  const utils = trpc.useUtils();
  const [error, setError] = useState<string | null>(null);
  const [pendingSignupCheckDone, setPendingSignupCheckDone] = useState(false);
  const pendingSignupCheckStartedRef = useRef(false);
  const bootstrapQ = trpc.bootstrap.get.useQuery(undefined, {
    enabled,
    retry: 1,
    staleTime: 120_000,
  });
  const statusQ = {
    data: bootstrapQ.data?.legal,
    isLoading: bootstrapQ.isLoading,
  };
  const acceptMutation = trpc.legal.acceptRequired.useMutation({
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        utils.bootstrap.get.invalidate(),
        utils.legal.status.invalidate(),
      ]);
    },
    onError: (err) => {
      setError(err.message || 'Unable to record acceptance. Please try again.');
    },
  });

  useEffect(() => {
    if (!enabled || !statusQ.data?.requiresAcceptance) {
      pendingSignupCheckStartedRef.current = false;
      setPendingSignupCheckDone(false);
      return;
    }
    if (pendingSignupCheckStartedRef.current) return;

    pendingSignupCheckStartedRef.current = true;

    void (async () => {
      const hasPendingSignupAcceptance = await consumePendingLegalSignupAcceptance();
      if (hasPendingSignupAcceptance) {
        acceptMutation.mutate(getLegalClientContext('signup'), {
          onSettled: () => setPendingSignupCheckDone(true),
        });
        return;
      }

      setPendingSignupCheckDone(true);
    })();
  }, [acceptMutation, enabled, statusQ.data?.requiresAcceptance]);

  if (!enabled || !statusQ.data?.requiresAcceptance) return null;
  if (!pendingSignupCheckDone) return null;

  const documentVersion = REQUIRED_LEGAL_DOCUMENTS[0]?.version ?? 'current';

  return (
    <Modal
      visible
      animationType="fade"
      transparent
      onRequestClose={() => undefined}
    >
      <View className="flex-1 items-center justify-center bg-black/60 px-4 py-8">
        <View className="max-h-full w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-900">
          <View className="border-b border-gray-200 px-5 py-5 dark:border-gray-800">
            <View className="flex-row items-start gap-3">
              <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
                <Ionicons name="document-text-outline" size={18} color="#b45309" />
              </View>
              <View className="flex-1">
                <Text className="text-xl font-semibold text-gray-950 dark:text-gray-50">
                  Review Per Diem's Terms
                </Text>
                <Text className="mt-2 text-sm leading-5 text-gray-600 dark:text-gray-300">
                  We need your acceptance of the current legal terms before you continue.
                </Text>
              </View>
            </View>
          </View>

          <ScrollView className="px-5" contentContainerClassName="py-5">
            {statusQ.isLoading ? (
              <LoadingSpinner />
            ) : (
              <View className="gap-4">
                <View className="rounded-xl bg-gray-50 px-4 py-4 dark:bg-gray-800">
                  <Text className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    Current version
                  </Text>
                  <Text className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {documentVersion}
                  </Text>
                </View>

                <Text className="text-sm leading-6 text-gray-700 dark:text-gray-200">
                  Per Diem is a personal finance and FIRE planning product. It helps you organize
                  data and model tradeoffs, but it does not replace qualified professional advice.
                </Text>

                <View className="gap-3 rounded-xl border border-gray-200 px-4 py-4 dark:border-gray-700">
                  <Text className="text-sm font-semibold text-gray-950 dark:text-gray-50">
                    Financial product acknowledgment
                  </Text>
                  <Text className="text-sm leading-6 text-gray-700 dark:text-gray-200">
                    {FINANCIAL_ACKNOWLEDGMENT_TEXT}
                  </Text>
                </View>

                <View className="gap-3">
                  <Pressable onPress={() => openExternalUrl(TERMS_URL)} className="self-start">
                    <Text className="text-sm font-medium text-green-700 dark:text-green-400">
                      Review Terms of Service
                    </Text>
                  </Pressable>
                  <Pressable onPress={() => openExternalUrl(PRIVACY_URL)} className="self-start">
                    <Text className="text-sm font-medium text-green-700 dark:text-green-400">
                      Review Privacy Policy
                    </Text>
                  </Pressable>
                </View>

                {error ? (
                  <View className="rounded-xl bg-red-50 px-4 py-3 dark:bg-red-900/30">
                    <Text className="text-sm text-red-700 dark:text-red-200">{error}</Text>
                  </View>
                ) : null}
              </View>
            )}
          </ScrollView>

          <View className="gap-3 border-t border-gray-200 px-5 py-5 dark:border-gray-800">
            <Button
              loading={acceptMutation.isPending}
              disabled={statusQ.isLoading}
              onPress={() => {
                acceptMutation.mutate(getLegalClientContext('login_gate'));
              }}
            >
              Accept and continue
            </Button>
            <Button
              variant="ghost"
              onPress={() => {
                void supabase.auth.signOut();
              }}
            >
              Sign out
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
}
